import { Card, Empty } from 'antd'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  SpendBucketSize,
  SpendChartPoint,
  formatBucketTick,
  formatUsd,
} from './spendModel'

type Props = {
  points: SpendChartPoint[]
  bucket: SpendBucketSize
  loading: boolean
}

const SpendTrendChart = ({ points, bucket, loading }: Props) => (
  <Card size="small" title="Spend over time" loading={loading}>
    {points.length === 0 ? (
      <Empty description="No spend recorded in this window" />
    ) : (
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={points} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="spendCost" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1677ff" stopOpacity={0.5} />
              <stop offset="100%" stopColor="#1677ff" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          {/*
            A numeric time axis, not a category axis: buckets with no spend are
            absent from the response rather than zero, so positioning by index
            would space an idle fortnight the same as consecutive days.
          */}
          <XAxis
            dataKey="t"
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(t: number) => formatBucketTick(t, bucket)}
            minTickGap={24}
          />
          <YAxis
            tickFormatter={(value: number) => formatUsd(String(value))}
            width={80}
          />
          <Tooltip
            labelFormatter={(t) => formatBucketTick(Number(t), bucket)}
            formatter={(value: number, name: string) => [
              formatUsd(String(value)),
              name,
            ]}
          />
          <Legend />
          {/*
            The uncached line sits behind, so the saving reads as the gap
            between what caching cost and what it would have cost without.
          */}
          <Area
            type="monotone"
            dataKey="uncached"
            name="Without caching"
            stroke="#bfbfbf"
            strokeDasharray="4 3"
            fill="none"
            dot={{ r: 2 }}
          />
          <Area
            type="monotone"
            dataKey="cost"
            name="Actual spend"
            stroke="#1677ff"
            fill="url(#spendCost)"
            dot={{ r: 2 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    )}
  </Card>
)

export default SpendTrendChart
